import {
	ChangeDetectorRef,
	Component,
	effect,
	EventEmitter,
	inject,
	input,
	Input,
	Output,
	signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import {
	IAssetTimeSeries,
	TimeSeriesWithoutPagination,
} from '@app/pages/cmms/components/assets/asset-time-series/misc/interfaces';
import { AuAvatarWithNameModule, AuRoundAvatarModule } from '@shared/ui-components';
import { MatMenuModule } from '@angular/material/menu';

import { AssetTimeSeriesService } from '@app/pages/cmms/components/assets/asset-time-series/misc/asset-time-series-service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { ChartSectionComponent } from '@app/pages/cmms/components/assets/asset-time-series/chart-with-settings/chart-section/chart-section.component';
import { NewTimeSeriesSettingsFormComponent } from '@app/pages/cmms/components/assets/asset-time-series/chart-with-settings/new-time-series-settings-form/new-time-series-settings-form.component';
import { EditSectionNameModalComponent } from '@app/pages/cmms/components/assets/asset-time-series/chart-with-settings/edit-section-name-modal/edit-section-name-modal.component';
import { catchError, first, map, mergeMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import {
	NewTimeseriesValueComponent,
	NewTimeseriesValue,
} from './new-timeseries-value/new-timeseries-value.component';
import { MatCardModule } from '@angular/material/card';
import { of } from 'rxjs';
import { TranslateService } from '@tolgee/ngx';
import { NgxTolgeeModule } from '@tolgee/ngx';
@Component({
	selector: 'asset-chart-with-settings',
	templateUrl: './asset-chart-with-settings.component.html',
	styleUrls: ['./asset-chart-with-settings.component.scss'],
	imports: [
		CommonModule,
		MatExpansionModule,
		MatIconModule,
		MatMenuModule,
		ChartSectionComponent,
		AuAvatarWithNameModule,
		AuRoundAvatarModule,
		MatTooltipModule,
		MatRippleModule,
		NewTimeSeriesSettingsFormComponent,
		EditSectionNameModalComponent,
		MatCardModule,
		NgxTolgeeModule,
	],
})
export class AssetChartWithSettingsComponent {
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly assetTimeSeriesService: AssetTimeSeriesService =
		inject(AssetTimeSeriesService);
	private readonly notify = inject(IconSnackBarService);
	private readonly dialog = inject(MatDialog);
	private readonly translateService = inject(TranslateService);

	@Input() set showNewGraphSettingsComponent(show: boolean) {
		this.showNewGraphComponent.set(show);
		if (show) {
			this.expanded.set(show);
		}
	}

	@Input() set assetPk(id: string) {
		this.currentAssetId = id;
	}

	showNewGraphComponent = signal(false);
	expanded = signal(true);

	// TODO use signal;
	currentAssetId: string;
	@Input() title: string;
	@Input() showSectionIcon = true;

	@Output() closed = new EventEmitter<void>();
	@Output() opened = new EventEmitter<void>();
	@Output() hideNewGraphSettings = new EventEmitter<unknown>();

	series = input<TimeSeriesWithoutPagination[]>();

	editChartNameModalVisible: boolean;
	editChartNameValue: { name: string; pk: string } | null = null;

	constructor() {
		effect(() => {
			// mat-expansion-panel can't seem to handle the expanded changing during initialization.
			// By starting expanded and closing it if there are no series it behaves as expected.
			this.expanded.set(!!this.series()?.length);
		});
	}

	newValueSubmitted(
		$event: { value: number; date: Date },
		chartPk: string,
		dontShowNotification?: boolean
	) {
		this.addTimeSeries($event, chartPk)
			.pipe(
				catchError(error => {
					this.notify.error(this.translateService.instant('failed_add_value'));
					console.error(error);
					return of();
				})
			)
			.subscribe(response => {
				const newSeries = this.series().map(chart =>
					chart.pk === chartPk ? mergeAndSortTimeSeries(chart, response) : chart
				);
				this.assetTimeSeriesService.updateStoredTimeSeriesData(newSeries);

				if (!dontShowNotification) {
					this.notify.success(this.translateService.instant('value_added_success'));
				}
				this.cdr.detectChanges();
			});
	}

	private addTimeSeries($event: { value: number; date: Date }, chartPk: string) {
		return this.assetTimeSeriesService
			.createAssetTimeSeriesData(chartPk, $event.value, $event.date)
			.pipe(first());
	}

	getLastTimeSeriesElement(graph): IAssetTimeSeries {
		const timeSeries = graph.timeSeries;
		if (timeSeries.length) {
			return timeSeries[timeSeries.length - 1];
		}

		return;
	}

	closeNewGraphComponent() {
		this.showNewGraphComponent.set(false);
		this.hideNewGraphSettings.emit();

		this.cdr.detectChanges();
	}

	createNewChart($event: {
		name: string;
		description?: string;
		unit?: string;
		value?: number;
	}) {
		this.showNewGraphComponent.set(false);
		this.hideNewGraphSettings.emit();
		this.cdr.detectChanges();
		this.assetTimeSeriesService
			.createChartSection(
				this.currentAssetId,
				$event.name,
				$event.description,
				$event.unit
			)
			.pipe(
				first(),
				mergeMap(newChart => {
					if (!$event.value) {
						return of(newChart);
					}
					return this.addTimeSeries(
						{ value: $event.value, date: new Date() },
						newChart.chart.pk
					).pipe(
						map(timeSeries => ({
							...newChart,
							chart: {
								...newChart.chart,
								timeSeriesCharts: {
									edges: timeSeries.map(ts => ({ node: ts })),
								},
							},
						}))
					);
				}),
				catchError(error => {
					this.notify.error(this.translateService.instant('failed_create_component'));
					console.error(error);
					return of();
				})
			)
			.subscribe(newChart => {
				const newSeries: TimeSeriesWithoutPagination = {
					pk: newChart.chart.pk,
					id: newChart.chart.id,
					name: newChart.chart.name,
					description: newChart.chart.description,
					settings: newChart.settings,
					timeSeries: newChart.chart.timeSeriesCharts.edges.map(edge => edge.node),
				};
				this.assetTimeSeriesService.updateStoredTimeSeriesData(
					[newSeries].concat(this.series())
				);
				this.notify.success(this.translateService.instant('component_created_success'));
			});
	}

	deleteChart(chart: TimeSeriesWithoutPagination) {
		this.assetTimeSeriesService
			.deleteChartSection(chart.pk)
			.pipe(
				first(),
				catchError(error => {
					this.notify.error(this.translateService.instant('failed_delete_component'));
					console.error(error);
					return of();
				})
			)
			.subscribe(() => {
				this.assetTimeSeriesService.updateStoredTimeSeriesData(
					this.series().filter(c => c.pk !== chart.pk)
				);

				this.notify.success(this.translateService.instant('component_deleted_success'));
			});
	}

	openAddValueDialog(chart: TimeSeriesWithoutPagination, $event?: MouseEvent) {
		if ($event) {
			$event.stopPropagation();
		}

		const dialogRef = this.dialog.open<NewTimeseriesValueComponent, NewTimeseriesValue>(
			NewTimeseriesValueComponent,
			{
				data: {
					component: chart.name,
					unit: chart?.settings?.unit,
					date: new Date(),
				},
			}
		);

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.newValueSubmitted(result, chart.pk);
			}
		});
	}

	editChartName(chart: any) {
		this.editChartNameValue = { name: chart.name, pk: chart.pk };
		this.editChartNameModalVisible = true;
	}

	closeEditChartNameModal() {
		this.editChartNameValue = null;
		this.editChartNameModalVisible = false;
	}

	saveNewChartName($event: { value: string; pk: string }) {
		this.assetTimeSeriesService
			.editAssetTimeSeries($event.pk, $event.value)
			.pipe(
				first(),
				catchError(error => {
					this.notify.error(
						this.translateService.instant('failed_update_component_name')
					);
					console.error(error);
					return of();
				})
			)
			.subscribe(() => {
				const newSeries = this.series().map(chart =>
					chart.pk === $event.pk ? { ...chart, name: $event.value } : chart
				);
				this.assetTimeSeriesService.updateStoredTimeSeriesData(newSeries);
				this.notify.success(this.translateService.instant('component_name_updated'));
			});

		this.closeEditChartNameModal();
	}
}

function mergeAndSortTimeSeries(
	chart: TimeSeriesWithoutPagination,
	newTimeSeries: IAssetTimeSeries[]
): TimeSeriesWithoutPagination {
	return {
		...chart,
		timeSeries: chart.timeSeries
			.concat(newTimeSeries)
			.sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime()),
	};
}
